import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import {
  Activity,
  AlertTriangle,
  Clock,
  DollarSign,
  Gauge,
  HeartPulse,
  TrendingUp,
  Wrench,
} from "lucide-react";
import React from "react";

interface KPICardsProps {
  data: any;
}

export const KPICards: React.FC<KPICardsProps> = ({ data }) => {
  // Config for KPI cards
  const topMetrics = [
    {
      title: "Uptime / Availability",
      displayValue: `${data?.overview?.assetAvailability?.value ?? "0"}%`,
      icon: <Activity className="h-5 w-5 text-green-600" />,
      tooltip:
        "The percentage of time this asset has been operational and available as scheduled during the selected period.",
    },
    {
      title: "MTBF",
      displayValue: `${data?.overview?.avgMtbf?.value ?? "0"} hrs`,
      icon: <Gauge className="h-5 w-5 text-blue-600" />,
      tooltip:
        "Mean Time Between Failures — higher values indicate better reliability.",
    },
    {
      title: "MTTR",
      displayValue: data?.overview?.avgMttr?.value ?? "NA",
      icon: <Wrench className="h-5 w-5 text-orange-600" />,
      tooltip:
        "Mean Time To Repair — the average time taken to restore the asset after a failure.",
    },
    {
      title: "Asset Performance Index",
      displayValue: `${data?.overview?.assetPerformanceRate?.value ?? "0"}%`,
      icon: <TrendingUp className="h-5 w-5 text-green-600" />,
      tooltip:
        "Overall operational effectiveness of the asset, combining reliability, availability, and efficiency.",
    },
  ];

  const bottomMetrics = [
    {
      title: "Asset Condition Index",
      displayValue: data?.overview?.assetHealthIndex?.value ?? "NA",
      icon: <HeartPulse className="h-5 w-5 text-emerald-600" />,
      tooltip:
        "The overall physical health of the asset based on recent condition assessment.",
    },
    {
      title: "Remaining Useful Life",
      displayValue: String(data?.overview?.avgRemainingLife?.value ?? "NA"),
      icon: <Clock className="h-5 w-5 text-yellow-600" />,
      tooltip:
        "Estimated time remaining before this asset reaches the end of its operational life.",
    },
    {
      title: "Overall Risk Score",
      displayValue: data?.overview?.overallRiskScore?.value ?? "NA",
      icon: <AlertTriangle className="h-5 w-5 text-red-600" />,
      tooltip:
        "The overall risk level, combining likelihood of failure with its potential consequences.",
    },
    {
      title: "Maintenance Cost Analysis",
      displayValue: data?.overview?.last_12MonthMaintainceData?.value ?? "NA",
      icon: <DollarSign className="h-5 w-5 text-indigo-600" />,
      tooltip:
        "Total maintenance costs for this asset during the selected period, compared to its budget.",
    },
  ];

  return (
    <div className="space-y-2">
      <h3 className="text-lg font-semibold text-gray-900">
        Core Performance Metrics
      </h3>

      {/* First Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {topMetrics.map((metric, idx) => (
          <QuickStatsCard key={idx} {...metric} count={undefined} />
        ))}
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {bottomMetrics.map((metric, idx) => (
          <QuickStatsCard key={idx} {...metric} count={undefined} />
        ))}
      </div>
    </div>
  );
};
